import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Check, X, Upload, Loader2 } from 'lucide-react';
import SelectImagesIcon from '@/assets/select-images-icon.svg';
import { cn } from '@/lib/utils';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { useBrandProductsEnhanced } from '@/hooks/useBrandProductsEnhanced';
import { useBrandAssets } from '@/hooks/useBrandAssets';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useBrandMoodboard } from '@/hooks/useBrandMoodboard';

interface ImageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFiles: File[];
  onFilesSelect: (files: File[]) => void;
  existingImages?: string[];
}

export function ImageSelectionModal({ 
  isOpen, 
  onClose, 
  selectedFiles, 
  onFilesSelect,
  existingImages = []
}: ImageSelectionModalProps) {
  const { currentBrandId } = useBrandManager();
  const { products, isLoading: productsLoading } = useBrandProductsEnhanced(currentBrandId || '');
  const { data: brandAssets, isLoading: assetsLoading } = useBrandAssets(currentBrandId || '');
  const { data: moodboardItems, isLoading: moodboardLoading } = useBrandMoodboard(currentBrandId || '');
  const [productSearchQuery, setProductSearchQuery] = useState('');

  const onDrop = (acceptedFiles: File[]) => {
    // Filter for image files only
    const imageFiles = acceptedFiles.filter(file => 
      file.type.startsWith('image/')
    );

    if (imageFiles.length !== acceptedFiles.length) {
      toast.error('Only image files are allowed');
    }

    if (imageFiles.length > 0) {
      onFilesSelect([...selectedFiles, ...imageFiles]);
      toast.success(`Added ${imageFiles.length} image(s)`);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    multiple: true
  });

  const removeFile = (indexToRemove: number) => {
    onFilesSelect(selectedFiles.filter((_, index) => index !== indexToRemove));
  };

  // Helper function to convert URL to File
  const urlToFile = async (url: string, filename: string): Promise<File | null> => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type });
    } catch (error) {
      console.error('Error converting URL to file:', error);
      toast.error('Failed to load image');
      return null;
    }
  };

  // Helper function to select image from URL
  const selectImageFromUrl = async (url: string, name: string) => {
    const file = await urlToFile(url, name);
    if (file) {
      onFilesSelect([...selectedFiles, file]);
      toast.success(`Added ${name}`);
    }
  };

  // Filter products based on search
  const filteredProducts = products?.filter(product => 
    productSearchQuery === '' || 
    product.product?.toLowerCase().includes(productSearchQuery.toLowerCase())
  ) || [];

  // Get product images
  const productImages = filteredProducts.flatMap(product => 
    product.images || []
  ).filter(Boolean);

  // Get logos and people (shareholders) from brand assets
  const logosAndPeopleImages = brandAssets?.filter(asset => 
    (asset.asset_type === 'logo' || asset.asset_type === 'person') && asset.file_url
  ) || [];

  // Get reference images from moodboard AND brand assets
  const moodboardReferenceImages = moodboardItems?.filter(item => 
    item.type === 'image' && item.url
  ) || [];
  
  const brandAssetReferenceImages = brandAssets?.filter(asset => 
    asset.asset_type === 'reference' && asset.file_url
  ) || [];
  
  // Combine both sources for reference images
  const referenceImages = [
    ...moodboardReferenceImages,
    ...brandAssetReferenceImages.map(asset => ({
      id: asset.id,
      url: asset.file_url!,
      title: asset.name,
      type: 'image'
    }))
  ];

  // Get background images from brand assets
  const backgroundImages = brandAssets?.filter(asset => 
    asset.asset_type === 'background' && asset.file_url
  ) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full h-full max-w-full max-h-full m-0 md:max-w-4xl md:max-h-[80vh] md:rounded-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <img src={SelectImagesIcon} alt="" className="h-5 w-5" />
            Select Images
          </DialogTitle>
        </DialogHeader>

        <Card>
          <CardHeader className="pb-3">
          </CardHeader>
          <CardContent className="pt-0">
            <Tabs defaultValue="uploaded" className="w-full">
              <TabsList className="grid grid-cols-2 gap-1 md:gap-2 md:grid-cols-5 h-auto py-1 md:py-2">
                <TabsTrigger value="uploaded" className="text-xs py-2 whitespace-normal h-auto">Custom Image</TabsTrigger>
                <TabsTrigger value="products" className="text-xs py-2 whitespace-normal h-auto">Product Images</TabsTrigger>
                <TabsTrigger value="brand" className="text-xs py-2 whitespace-normal h-auto">Brand Assets</TabsTrigger>
                <TabsTrigger value="reference" className="text-xs py-2 whitespace-normal h-auto">Reference Images</TabsTrigger>
                <TabsTrigger value="backgrounds" className="text-xs py-2 whitespace-normal h-auto col-span-2 md:col-span-1">Backgrounds</TabsTrigger>
              </TabsList>
              
              {/* Previously Uploaded Images Tab */}
              <TabsContent value="uploaded" className="mt-3 space-y-4">
                {/* Upload Area */}
                <div
                  {...getRootProps()}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                    isDragActive 
                      ? "border-primary bg-primary/5" 
                      : "border-muted-foreground/30 hover:border-primary"
                  )}
                >
                  <input {...getInputProps()} />
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {isDragActive
                      ? "Drop the images here..."
                      : "Drag & drop images here, or click to select"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG, JPG, JPEG, GIF, WEBP up to 20MB each
                  </p>
                </div>

                {/* Existing Uploaded Images from Post */}
                {existingImages.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Previously Uploaded to Post ({existingImages.length})</p>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 max-h-64 overflow-y-auto">
                      {existingImages.map((imageUrl, index) => (
                        <div 
                          key={`existing-${index}`}
                          className="relative cursor-pointer group"
                          onClick={() => selectImageFromUrl(imageUrl, `uploaded-image-${index + 1}.jpg`)}
                        >
                          <img 
                            src={imageUrl} 
                            alt={`Uploaded ${index + 1}`} 
                            className="w-full h-20 object-cover rounded-md bg-gray-50 group-hover:opacity-80 transition-opacity"
                          />
                          <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 rounded-md flex items-center justify-center transition-all">
                            <Check className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Selected for Generation ({selectedFiles.length})</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onFilesSelect([])}
                      >
                        Clear All
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 max-h-64 overflow-y-auto">
                      {selectedFiles.map((file, index) => {
                        if (!file || !(file instanceof File)) {
                          return null;
                        }
                        try {
                          const objectUrl = URL.createObjectURL(file);
                          return (
                            <div 
                              key={index} 
                              className="relative cursor-pointer rounded-lg border-2 border-primary ring-2 ring-primary/20 transition-all"
                            >
                              <img 
                                src={objectUrl} 
                                alt={`Selected ${index + 1}`} 
                                className="w-full h-20 object-cover rounded-md bg-gray-50"
                              />
                              <div className="absolute inset-0 bg-primary/20 rounded-md flex items-center justify-center">
                                <Check className="h-4 w-4 text-primary" />
                              </div>
                              <button
                                className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center hover:bg-destructive/90 transition-colors"
                                onClick={() => removeFile(index)}
                                aria-label="Remove image"
                              >
                                <X className="h-3 w-3" />
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 rounded-b-md truncate">
                                {file.name}
                              </div>
                            </div>
                          );
                        } catch (error) {
                          console.error('Error creating object URL for file:', file, error);
                          return null;
                        }
                      }).filter(Boolean)}
                    </div>
                  </div>
                )}

                {existingImages.length === 0 && selectedFiles.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No images uploaded yet. Upload images above or flip the card to upload images.
                  </div>
                )}
              </TabsContent>

              {/* Product Images Tab */}
              <TabsContent value="products" className="mt-3 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search products..."
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    className="pl-10 h-8 text-sm"
                  />
                </div>
                
                {productsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading products...</span>
                  </div>
                ) : productImages.length > 0 ? (
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3 max-h-64 overflow-y-auto">
                    {productImages.map((imageUrl, index) => (
                      <div 
                        key={index}
                        className="relative cursor-pointer group"
                        onClick={() => selectImageFromUrl(imageUrl, `product-image-${index + 1}.jpg`)}
                      >
                        <img 
                          src={imageUrl} 
                          alt={`Product ${index + 1}`} 
                          className="w-full h-20 object-cover rounded-md bg-gray-50 group-hover:opacity-80 transition-opacity"
                        />
                        <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 rounded-md flex items-center justify-center transition-all">
                          <Check className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    {productSearchQuery ? 'No products match your search' : 'No product images available'}
                  </div>
                )}
              </TabsContent>

              {/* Brand Assets Tab */}
              <TabsContent value="brand" className="mt-3">
                {assetsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading brand assets...</span>
                  </div>
                ) : logosAndPeopleImages.length > 0 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 max-h-64 overflow-y-auto">
                      {logosAndPeopleImages.map((asset, index) => (
                        <div 
                          key={asset.id}
                          className="relative cursor-pointer group"
                          onClick={() => selectImageFromUrl(asset.file_url!, `${asset.name || 'logo'}.${asset.file_url!.split('.').pop()}`)}
                        >
                          <img 
                            src={asset.file_url!} 
                            alt={asset.name || `Logo ${index + 1}`} 
                            className="w-full h-20 object-cover rounded-md bg-gray-50 group-hover:opacity-80 transition-opacity"
                          />
                          <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 rounded-md flex items-center justify-center transition-all">
                            <Check className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 rounded-b-md truncate">
                            {asset.name || 'Logo'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No brand assets available
                  </div>
                )}
              </TabsContent>

              {/* Reference Images Tab */}
              <TabsContent value="reference" className="mt-3">
                {moodboardLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading reference images...</span>
                  </div>
                ) : referenceImages.length > 0 ? (
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3 max-h-64 overflow-y-auto">
                    {referenceImages.map((item, index) => (
                      <div 
                        key={item.id}
                        className="relative cursor-pointer group"
                        onClick={() => selectImageFromUrl(item.url, `${item.title || 'reference'}.${item.url.split('.').pop()}`)}
                      >
                        <img 
                          src={item.url} 
                          alt={item.title || `Reference ${index + 1}`} 
                          className="w-full h-20 object-cover rounded-md bg-gray-50 group-hover:opacity-80 transition-opacity"
                        />
                        <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 rounded-md flex items-center justify-center transition-all">
                          <Check className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 rounded-b-md truncate">
                          {item.title || 'Reference'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No reference images available
                  </div>
                )}
              </TabsContent>

              {/* Backgrounds Tab */}
              <TabsContent value="backgrounds" className="mt-3">
                {assetsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading backgrounds...</span>
                  </div>
                ) : backgroundImages.length > 0 ? (
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3 max-h-64 overflow-y-auto">
                    {backgroundImages.map((asset, index) => (
                      <div 
                        key={asset.id}
                        className="relative cursor-pointer group"
                        onClick={() => selectImageFromUrl(asset.file_url!, `${asset.name || 'background'}.${asset.file_url!.split('.').pop()}`)}
                      >
                        <img 
                          src={asset.file_url!} 
                          alt={asset.name || `Background ${index + 1}`} 
                          className="w-full h-20 object-cover rounded-md bg-gray-50 group-hover:opacity-80 transition-opacity"
                        />
                        <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 rounded-md flex items-center justify-center transition-all">
                          <Check className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 rounded-b-md truncate">
                          {asset.name || 'Background'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No background images available
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onClose} disabled={selectedFiles.length === 0}>
            Use Selected Images ({selectedFiles.length})
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}